// Veyl — konturbasert pikselering av bilder OG videoer.
// Alt kjører i nettleseren. Filen forlater aldri brukerens enhet.
import React, { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Upload, Download, Trash2, ScanFace, PenLine, Undo2,
  Loader2, Shield, Info, Eye, EyeOff, PersonStanding, Film, Image as ImageIcon,
  Play, Pause, Square,
} from "lucide-react";
import {
  expandPolygon, detectFaces, detectSkinMask, pixelateWithMask,
  polygonsToMask, combineMasks, intersectMasks,
  extractTemplate, findTemplate, polygonCentroid, translatePolygon,
  blendTemplate, scalePolygon,
} from "../lib/veyl";
import { loadOpenCV, OpticalFlowTracker } from "../lib/veyl-opencv";

// Overordnet modus: bilde eller video
const MODE_IMAGE = "image";
const MODE_VIDEO = "video";

export default function Veyl() {
  const [mediaType, setMediaType] = useState(null); // 'image' | 'video'
  const [image, setImage] = useState(null); // HTMLImageElement
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoSize, setVideoSize] = useState({ w: 0, h: 0 });
  const [fileName, setFileName] = useState("");
  const [polygons, setPolygons] = useState([]); // [{points, type}]
  const [skinMask, setSkinMask] = useState(null); // HTMLCanvasElement | null
  const [drawing, setDrawing] = useState(false);
  const [currentPoints, setCurrentPoints] = useState([]);
  const [blockSize, setBlockSize] = useState(8);
  const [mode, setMode] = useState("pixelate");
  const [detecting, setDetecting] = useState(false);
  const [detectingSkin, setDetectingSkin] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tool, setTool] = useState("draw");
  const [recording, setRecording] = useState(false);
  const [recordProgress, setRecordProgress] = useState(0);
  const [autoTrackFaces, setAutoTrackFaces] = useState(false);
  // Følger brukertegnede omriss automatisk gjennom video via template-matching
  const [trackRegions, setTrackRegions] = useState(true);
  // Når true: pikelerer kun HUD INNENFOR markerte polygoner (intersection).
  const [skinInSelection, setSkinInSelection] = useState(true);
  // Gjeldende senter per polygon (oppdateres per frame under tracking)
  const trackedCentersRef = useRef(new Map()); // polygonId -> {x, y}
  // OpenCV optical flow trackere per polygon-ID
  const trackersRef = useRef(new Map()); // polygonId -> OpticalFlowTracker
  const [cvLoaded, setCvLoaded] = useState(false);
  const [cvLoading, setCvLoading] = useState(false);
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const videoRef = useRef(null);
  const rafRef = useRef(null);

  // --- Fil-opplasting ---
  const handleUpload = (file) => {
    if (!file) return;
    if (file.type.startsWith("image/")) {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        setImage(img);
        setMediaType(MODE_IMAGE);
        setFileName(file.name);
        resetSladder();
      };
      img.src = url;
    } else if (file.type.startsWith("video/")) {
      const url = URL.createObjectURL(file);
      setVideoUrl(url);
      setMediaType(MODE_VIDEO);
      setFileName(file.name);
      resetSladder();
    } else {
      toast.error("Kun bilde- eller videofiler støttes");
    }
  };

  const resetSladder = () => {
    setPolygons([]);
    setSkinMask(null);
    setCurrentPoints([]);
    setShowOriginal(false);
  };

  const onFileInput = (e) => handleUpload(e.target.files?.[0]);
  const onDrop = (e) => {
    e.preventDefault();
    handleUpload(e.dataTransfer.files?.[0]);
  };

  // --- Render til canvas ---
  const buildFinalMask = (polys, w, h) => {
    const polyExpanded = polys.map((p) => ({
      ...p,
      points: p.type === "face" || p.type === "face-auto"
        ? expandPolygon(p.points, 1.12)
        : p.points,
    }));
    const polyMask =
      polyExpanded.length > 0
        ? polygonsToMask(w, h, polyExpanded.map((p) => p.points))
        : null;

    // Skjæringspunkt-modus: kun hud innenfor markering
    if (skinInSelection && skinMask && polyMask) {
      return intersectMasks(polyMask, skinMask);
    }
    // Union-modus: alt markert + all hud pikeleres
    return combineMasks(polyMask, skinMask);
  };

  const renderFrame = useCallback((sourceEl, w, h) => {
    const canvas = canvasRef.current;
    if (!canvas || !sourceEl) return;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(sourceEl, 0, 0, w, h);

    if (!showOriginal) {
      const finalMask = buildFinalMask(polygons, w, h);
      if (finalMask) {
        pixelateWithMask(canvas, finalMask, blockSize, mode);
      }
    }
  }, [polygons, skinMask, blockSize, mode, showOriginal, skinInSelection]);

  const renderImage = useCallback(() => {
    if (mediaType !== MODE_IMAGE || !image) return;
    renderFrame(image, image.naturalWidth, image.naturalHeight);
    drawOverlay();
  }, [mediaType, image, renderFrame]);

  // Video: kontinuerlig render av gjeldende frame
  const renderVideoFrame = useCallback(async () => {
    if (mediaType !== MODE_VIDEO || !videoRef.current) return;
    const v = videoRef.current;
    if (!v.videoWidth || !v.videoHeight || v.readyState < 2) {
      rafRef.current = requestAnimationFrame(renderVideoFrame);
      return;
    }

    // Tegn nåværende frame til et midlertidig canvas for tracking-lookup
    const canvas = canvasRef.current;
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(v, 0, 0);

    // Auto-tracking av ansikter per frame (kun hvis aktivert)
    let framePolygons = polygons;
    if (autoTrackFaces && !v.paused) {
      try {
        const faces = await detectFaces(v, v.videoWidth, v.videoHeight);
        framePolygons = [
          ...polygons,
          ...faces.map((points) => ({ points, type: "face-auto" })),
        ];
      } catch { /* ignorer feil per frame */ }
    }

    // Template-tracking av brukertegnede omriss (kun mens videoen spilles)
    if (trackRegions && !v.paused) {
      let frameCounter = (trackedCentersRef.current.get("__frame") || 0) + 1;
      trackedCentersRef.current.set("__frame", frameCounter);

      framePolygons = framePolygons.map((p) => {
        if (p.type !== "manual" || !p.id) return p;

        // Foretrukket: OpenCV optical flow tracker
        if (p.hasCvTracker && trackersRef.current.has(p.id)) {
          const tracker = trackersRef.current.get(p.id);
          const delta = tracker.update(canvas);
          if (delta) {
            const cx = p.initialCentroid.x + delta.dx;
            const cy = p.initialCentroid.y + delta.dy;
            trackedCentersRef.current.set(p.id, { x: cx, y: cy });
            let updatedPoints = translatePolygon(p.points, delta.dx, delta.dy);
            if (delta.scale && Math.abs(delta.scale - 1.0) > 0.02) {
              updatedPoints = scalePolygon(updatedPoints, delta.scale, { x: cx, y: cy });
            }
            // Auto-margin 15 % for å skjule små tracking-drift
            updatedPoints = scalePolygon(updatedPoints, 1.15, { x: cx, y: cy });
            return { ...p, points: updatedPoints };
          }
          // Tracker mistet subjektet → fall gjennom til template-fallback
        }

        // Fallback: SSD template-matching (hvis OpenCV ikke tilgjengelig)
        if (!p.template) return p;
        const prev = trackedCentersRef.current.get(p.id) || p.initialCentroid;
        const match = findTemplate(canvas, p.template, prev.x, prev.y, {
          searchRadius: 24,
          scales: [0.92, 1.0, 1.08],
          step: 1,
          sampleStride: 1,
        });
        if (!match) return p;
        const fdx = match.cx - prev.x;
        const fdy = match.cy - prev.y;
        if (Math.hypot(fdx, fdy) > 80) return p;
        trackedCentersRef.current.set(p.id, { x: match.cx, y: match.cy });
        if (frameCounter % 8 === 0) {
          p.template = blendTemplate(p.template, canvas, match.cx, match.cy, 0.15);
        }
        const scaleFactor = match.scale || 1.0;
        let updatedPoints = translatePolygon(
          p.points,
          match.cx - p.initialCentroid.x,
          match.cy - p.initialCentroid.y,
        );
        if (scaleFactor !== 1.0) {
          updatedPoints = scalePolygon(updatedPoints, scaleFactor, { x: match.cx, y: match.cy });
        }
        updatedPoints = scalePolygon(updatedPoints, 1.2, { x: match.cx, y: match.cy });
        return { ...p, points: updatedPoints };
      });
    }

    // Bruk pikselering
    if (!showOriginal) {
      const finalMask = buildFinalMask(framePolygons, v.videoWidth, v.videoHeight);
      if (finalMask) {
        pixelateWithMask(canvas, finalMask, blockSize, mode);
      }
    }
    drawOverlay();
    rafRef.current = requestAnimationFrame(renderVideoFrame);
  }, [mediaType, polygons, skinMask, blockSize, mode, showOriginal, autoTrackFaces, skinInSelection, trackRegions]);

  useEffect(() => {
    if (mediaType === MODE_IMAGE) renderImage();
  }, [renderImage, mediaType]);

  useEffect(() => {
    if (mediaType === MODE_VIDEO) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(renderVideoFrame);
    }
    return () => cancelAnimationFrame(rafRef.current);
  }, [renderVideoFrame, mediaType]);

  // --- Overlay: viser markeringer + pågående tegning ---
  const drawOverlay = () => {
    if (!overlayRef.current || !canvasRef.current) return;
    const overlay = overlayRef.current;
    overlay.width = canvasRef.current.width;
    overlay.height = canvasRef.current.height;
    const ctx = overlay.getContext("2d");
    ctx.clearRect(0, 0, overlay.width, overlay.height);

    for (const poly of polygons) {
      if (poly.points.length < 2) continue;
      ctx.beginPath();
      ctx.moveTo(poly.points[0].x, poly.points[0].y);
      for (let i = 1; i < poly.points.length; i++) {
        ctx.lineTo(poly.points[i].x, poly.points[i].y);
      }
      ctx.closePath();
      ctx.strokeStyle = poly.type === "face" ? "#c2185b" : "#d4af37";
      ctx.lineWidth = Math.max(2, overlay.width / 500);
      ctx.stroke();
    }

    if (skinMask) {
      // Vis skin-mask omriss
      ctx.save();
      ctx.globalAlpha = 0.35;
      ctx.drawImage(skinMask, 0, 0);
      ctx.restore();
    }

    if (currentPoints.length > 0) {
      ctx.beginPath();
      ctx.moveTo(currentPoints[0].x, currentPoints[0].y);
      for (let i = 1; i < currentPoints.length; i++) {
        ctx.lineTo(currentPoints[i].x, currentPoints[i].y);
      }
      ctx.strokeStyle = "#22c55e";
      ctx.lineWidth = Math.max(2, overlay.width / 500);
      ctx.stroke();
    }
  };

  useEffect(drawOverlay, [polygons, currentPoints, skinMask]);

  // --- Auto-detektering ---
  const runFaceDetection = async () => {
    if (detecting) return;
    setDetecting(true);
    try {
      const el = mediaType === MODE_IMAGE ? image : videoRef.current;
      const w = mediaType === MODE_IMAGE ? image.naturalWidth : videoRef.current.videoWidth;
      const h = mediaType === MODE_IMAGE ? image.naturalHeight : videoRef.current.videoHeight;
      if (!el || !w || !h) {
        toast.error("Ingen kilde å analysere");
        return;
      }
      const faces = await detectFaces(el, w, h);
      if (faces.length === 0) {
        toast.info("Fant ingen ansikter — tegn manuelt med pennen");
      } else {
        setPolygons((prev) => [
          ...prev,
          ...faces.map((points) => ({ points, type: "face" })),
        ]);
        toast.success(`Fant ${faces.length} ansikt${faces.length > 1 ? "er" : ""}`);
      }
    } catch (e) {
      toast.error("Auto-detektering feilet: " + (e.message || e));
    } finally {
      setDetecting(false);
    }
  };

  const runSkinDetection = async () => {
    if (detectingSkin) return;
    setDetectingSkin(true);
    try {
      const el = mediaType === MODE_IMAGE ? image : videoRef.current;
      const w = mediaType === MODE_IMAGE ? image.naturalWidth : videoRef.current.videoWidth;
      const h = mediaType === MODE_IMAGE ? image.naturalHeight : videoRef.current.videoHeight;
      if (!el || !w || !h) {
        toast.error("Ingen kilde å analysere");
        return;
      }
      const mask = await detectSkinMask(el, w, h, { includeBodySkin: true });
      if (!mask) {
        toast.info("Fant ingen hud/kropp");
      } else {
        setSkinMask(mask);
        toast.success("Kropp/hud markert");
      }
    } catch (e) {
      toast.error("Kropps-detektering feilet: " + (e.message || e));
    } finally {
      setDetectingSkin(false);
    }
  };

  const clearSkinMask = () => setSkinMask(null);

  // --- Musehåndtering ---
  // Regner om klikk-koordinater i display til canvas-piksler.
  // Håndterer letterboxing fra object-contain (svarte kanter for portrett/landskap).
  const getCanvasPoint = (e) => {
    const overlay = overlayRef.current;
    const canvas = canvasRef.current;
    if (!overlay || !canvas) return { x: 0, y: 0 };
    const rect = overlay.getBoundingClientRect();
    const canvasAspect = canvas.width / canvas.height;
    const displayAspect = rect.width / rect.height;

    let effW, effH, offX, offY;
    if (canvasAspect > displayAspect) {
      // Video bredere enn container → letterbox topp/bunn
      effW = rect.width;
      effH = rect.width / canvasAspect;
      offX = 0;
      offY = (rect.height - effH) / 2;
    } else {
      // Video høyere enn container → letterbox venstre/høyre
      effH = rect.height;
      effW = rect.height * canvasAspect;
      offX = (rect.width - effW) / 2;
      offY = 0;
    }
    const scaleX = canvas.width / effW;
    const scaleY = canvas.height / effH;
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (cx - rect.left - offX) * scaleX,
      y: (cy - rect.top - offY) * scaleY,
    };
  };
  const onPointerDown = (e) => {
    if (tool !== "draw" || !canvasRef.current) return;
    e.preventDefault();
    setDrawing(true);
    setCurrentPoints([getCanvasPoint(e)]);
  };
  const onPointerMove = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const p = getCanvasPoint(e);
    setCurrentPoints((prev) => {
      const last = prev[prev.length - 1];
      if (last && Math.hypot(p.x - last.x, p.y - last.y) < 3) return prev;
      return [...prev, p];
    });
  };
  // Last inn OpenCV når video åpnes + tracking er aktivert
  useEffect(() => {
    if (mediaType === MODE_VIDEO && trackRegions && !cvLoaded && !cvLoading) {
      setCvLoading(true);
      loadOpenCV()
        .then(() => {
          setCvLoaded(true);
          toast.success("Profesjonell tracker klar (OpenCV)");
        })
        .catch((err) => {
          toast.error("Kunne ikke laste OpenCV — bruker fallback-tracker");
          console.warn("OpenCV load failed:", err);
        })
        .finally(() => setCvLoading(false));
    }
  }, [mediaType, trackRegions, cvLoaded, cvLoading]);

  const onPointerUp = () => {
    if (!drawing) return;
    setDrawing(false);
    if (currentPoints.length >= 3) {
      const id = `manual-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const centroid = polygonCentroid(currentPoints);
      let template = null;
      let tracker = null;
      // Video-modus: sett opp tracker
      if (mediaType === MODE_VIDEO && canvasRef.current) {
        // Fallback template alltid (hvis OpenCV feiler)
        template = extractTemplate(canvasRef.current, centroid.x, centroid.y, 40);
        // Prøv OpenCV-tracker (best kvalitet)
        if (cvLoaded && window.cv) {
          try {
            const t = new OpticalFlowTracker(window.cv);
            if (t.init(canvasRef.current, currentPoints)) {
              tracker = t;
              trackersRef.current.set(id, t);
            } else {
              t.dispose();
            }
          } catch (e) {
            console.warn("Tracker init feilet:", e);
          }
        }
      }
      const newPoly = {
        id,
        points: currentPoints,
        type: "manual",
        template,
        hasCvTracker: !!tracker,
        initialCentroid: centroid,
      };
      trackedCentersRef.current.set(id, { ...centroid });
      setPolygons((prev) => [...prev, newPoly]);
    }
    setCurrentPoints([]);
  };

  // Reset alle trackere til initial-posisjon + re-initialiser OpenCV-trackere fra frame 0
  const resetAllTrackers = () => {
    polygons.forEach((p) => {
      if (p.id && p.initialCentroid) {
        trackedCentersRef.current.set(p.id, { ...p.initialCentroid });
      }
      // Dispose gammel + re-init ny OpenCV tracker fra current frame (frame 0)
      const oldT = trackersRef.current.get(p.id);
      if (oldT) {
        oldT.dispose();
        trackersRef.current.delete(p.id);
      }
      if (p.hasCvTracker && cvLoaded && window.cv && canvasRef.current) {
        // Vent litt så canvasen rekker å tegne frame 0
        setTimeout(() => {
          try {
            const nt = new OpticalFlowTracker(window.cv);
            if (nt.init(canvasRef.current, p.points)) {
              trackersRef.current.set(p.id, nt);
            } else {
              nt.dispose();
            }
          } catch (e) { /* ignorer */ }
        }, 80);
      }
    });
  };

  const undoLast = () => {
    setPolygons((prev) => {
      const removed = prev[prev.length - 1];
      if (removed?.id) {
        trackedCentersRef.current.delete(removed.id);
        const tracker = trackersRef.current.get(removed.id);
        if (tracker) { tracker.dispose(); trackersRef.current.delete(removed.id); }
      }
      return prev.slice(0, -1);
    });
  };
  const clearAll = () => {
    trackedCentersRef.current.clear();
    for (const t of trackersRef.current.values()) {
      try { t.dispose(); } catch { /* ignorer */ }
    }
    trackersRef.current.clear();
    setPolygons([]);
    setCurrentPoints([]);
    setSkinMask(null);
  };

  // --- Lagre bilde ---
  const downloadImage = () => {
    if (!image || !canvasRef.current) return;
    setBusy(true);
    // Force render uten showOriginal
    const prevShow = showOriginal;
    setShowOriginal(false);
    setTimeout(() => {
      canvasRef.current.toBlob((blob) => {
        if (!blob) { setBusy(false); toast.error("Kunne ikke lage bilde"); return; }
        const a = document.createElement("a");
        const url = URL.createObjectURL(blob);
        a.href = url;
        const base = fileName.replace(/\.[^.]+$/, "") || "veyl";
        a.download = `${base}_veyl.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setBusy(false);
        setShowOriginal(prevShow);
        toast.success("Lagret! Filen forlot aldri enheten din.");
      }, "image/png");
    }, 100);
  };

  // --- Lagre video ---
  const downloadVideo = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    setBusy(true);
    setRecording(true);
    setRecordProgress(0);
    const v = videoRef.current;
    const canvas = canvasRef.current;
    const stream = canvas.captureStream(30);
    const chunks = [];
    // Prøv mp4 først, fallback til webm
    let mimeType = "video/mp4";
    if (!MediaRecorder.isTypeSupported(mimeType)) {
      mimeType = "video/webm;codecs=vp9";
      if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = "video/webm";
    }
    const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 8_000_000 });
    recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const base = fileName.replace(/\.[^.]+$/, "") || "veyl";
      const ext = mimeType.includes("mp4") ? "mp4" : "webm";
      a.download = `${base}_veyl.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setBusy(false);
      setRecording(false);
      setRecordProgress(0);
      toast.success("Video lagret. Filen forlot aldri enheten din.");
    };

    recorder.start();
    // Reset alle trackere til initial så eksport starter fra riktig sted
    resetAllTrackers();
    v.currentTime = 0;
    await v.play();
    const dur = v.duration || 0;
    const progressInterval = setInterval(() => {
      setRecordProgress(Math.min(100, (v.currentTime / dur) * 100));
    }, 200);
    v.onended = () => {
      clearInterval(progressInterval);
      recorder.stop();
      v.onended = null;
    };
  };

  const download = () => (mediaType === MODE_IMAGE ? downloadImage() : downloadVideo());
  const hasContent = polygons.length > 0 || skinMask !== null;

  return (
    <div className="min-h-screen bg-[var(--noir)] text-[var(--cream)]">
      <header className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-6 h-6 text-[var(--magenta)]" />
            <span className="font-meta text-xs tracking-[0.2em] text-[var(--cream-soft)]">
              NORSK EROTIKK · VEYL
            </span>
          </div>
          <h1 className="font-editorial text-3xl sm:text-4xl lg:text-5xl leading-tight">
            Skjul ansikt, kropp og kjennetegn — <span className="text-[var(--magenta)]">trygt</span>.
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-[var(--cream-soft)]">
            Vanlig blur kan reverseres med AI. Veyl bruker <b>destruktiv pikselering</b> som følger naturlige konturer.
            Fungerer på både bilder og videoer. Alt kjører lokalt — filen forlater aldri enheten.
          </p>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {!mediaType ? (
          <div
            data-testid="veyl-dropzone"
            onDrop={onDrop}
            onDragOver={(e) => e.preventDefault()}
            className="border-2 border-dashed border-white/20 hover:border-[var(--magenta)]/60 transition p-16 text-center cursor-pointer"
            onClick={() => document.getElementById("veyl-file").click()}
          >
            <Upload className="w-12 h-12 mx-auto mb-4 text-[var(--cream-soft)]" />
            <p className="font-editorial text-2xl mb-2">Slipp bilde eller video her</p>
            <p className="text-sm text-[var(--cream-soft)]">
              Eller <span className="text-[var(--magenta)] underline">velg fra enheten</span>
            </p>
            <p className="mt-6 text-xs text-[var(--cream-faint)]">
              Bilde (JPG/PNG/WEBP) · Video (MP4/MOV/WEBM) · behandles kun lokalt
            </p>
            <input
              id="veyl-file"
              type="file"
              accept="image/*,video/*"
              className="hidden"
              onChange={onFileInput}
              data-testid="veyl-file-input"
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
            <div className="relative bg-black/40 border border-white/10 overflow-hidden select-none"
                 style={{ minHeight: "300px", maxHeight: "80vh" }}>
              {mediaType === MODE_VIDEO && (
                <video
                  ref={videoRef}
                  src={videoUrl}
                  playsInline
                  muted
                  preload="auto"
                  style={{ position: "absolute", left: "-99999px", width: "1px", height: "1px", opacity: 0, pointerEvents: "none" }}
                  onLoadedMetadata={(e) => {
                    setVideoSize({ w: e.target.videoWidth, h: e.target.videoHeight });
                  }}
                  onLoadedData={async (e) => {
                    // Tving frem første frame: spill en kort stund, pause deretter.
                    // Uten dette blir canvasen sort inntil brukeren manuelt trykker play.
                    const v = e.target;
                    try {
                      await v.play();
                      // Vent til vi har minst én dekodet frame
                      await new Promise((r) => setTimeout(r, 80));
                      v.pause();
                      v.currentTime = 0;
                    } catch { /* autoplay-blokker eller allerede pauset */ }
                    if (videoRef.current) {
                      cancelAnimationFrame(rafRef.current);
                      rafRef.current = requestAnimationFrame(renderVideoFrame);
                    }
                  }}
                />
              )}
              <canvas ref={canvasRef}
                      className="w-full h-full object-contain block pointer-events-none"
                      style={{ maxHeight: "80vh" }} />
              <canvas ref={overlayRef}
                      className="absolute inset-0 w-full h-full object-contain touch-none"
                      style={{ maxHeight: "80vh", cursor: tool === "draw" ? "crosshair" : "default" }}
                      onMouseDown={onPointerDown} onMouseMove={onPointerMove}
                      onMouseUp={onPointerUp} onMouseLeave={onPointerUp}
                      onTouchStart={onPointerDown} onTouchMove={onPointerMove} onTouchEnd={onPointerUp}
                      data-testid="veyl-canvas" />
              {(detecting || detectingSkin) && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                  <div className="flex items-center gap-3 text-sm">
                    <Loader2 className="w-5 h-5 animate-spin text-[var(--magenta)]" />
                    {detecting ? "Detekterer ansikter…" : "Detekterer kropp…"}
                  </div>
                </div>
              )}
              {recording && (
                <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center gap-4">
                  <div className="w-8 h-8 rounded-full bg-red-600 animate-pulse" />
                  <div className="font-meta text-xs tracking-[0.2em]">EKSPORTERER VIDEO · {Math.round(recordProgress)}%</div>
                  <div className="w-64 h-1 bg-white/10">
                    <div className="h-full bg-[var(--magenta)] transition-all" style={{ width: `${recordProgress}%` }} />
                  </div>
                </div>
              )}
              {/* Video-avspilling controls (kun i video-modus) */}
              {mediaType === MODE_VIDEO && !recording && (
                <div className="absolute bottom-2 left-2 right-2 flex items-center gap-2 bg-black/70 px-3 py-2 backdrop-blur">
                  <button
                    onClick={() => {
                      const v = videoRef.current;
                      if (v.paused) v.play(); else v.pause();
                    }}
                    className="text-[var(--cream)] hover:text-[var(--magenta)]"
                    data-testid="veyl-video-play"
                  >
                    {videoRef.current?.paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => {
                      videoRef.current.pause();
                      videoRef.current.currentTime = 0;
                      resetAllTrackers();
                    }}
                    className="text-[var(--cream)] hover:text-[var(--magenta)]"
                    data-testid="veyl-video-stop"
                  >
                    <Square className="w-4 h-4" />
                  </button>
                  <span className="font-meta text-[10px] text-[var(--cream-soft)] ml-2">
                    {videoSize.w}×{videoSize.h}
                  </span>
                </div>
              )}
            </div>

            <aside className="space-y-4">
              {/* Auto-detekter */}
              <div className="border border-white/10 p-4 space-y-2">
                <div className="font-meta text-xs text-[var(--cream-soft)] mb-2 tracking-[0.15em]">
                  AUTO-DETEKTER
                </div>
                <button
                  onClick={runFaceDetection}
                  disabled={detecting}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[var(--magenta)] text-white px-4 py-2 text-sm font-meta tracking-[0.1em] hover:opacity-90 transition disabled:opacity-50"
                  data-testid="veyl-detect-faces"
                >
                  <ScanFace className="w-4 h-4" />
                  Finn ansikter
                </button>
                <button
                  onClick={skinMask ? clearSkinMask : runSkinDetection}
                  disabled={detectingSkin}
                  className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-meta tracking-[0.1em] transition disabled:opacity-50 border ${
                    skinMask
                      ? "bg-[var(--gold)] text-[var(--noir)] border-[var(--gold)]"
                      : "border-white/20 text-[var(--cream)] hover:border-white/40"
                  }`}
                  data-testid="veyl-detect-skin"
                >
                  <PersonStanding className="w-4 h-4" />
                  {skinMask ? "Fjern kropp-markering" : "Finn kropp / hud"}
                </button>
                {skinMask && (
                  <label className="flex items-start gap-2 text-xs cursor-pointer pt-2 border-t border-white/5 mt-2">
                    <input
                      type="checkbox"
                      checked={skinInSelection}
                      onChange={(e) => setSkinInSelection(e.target.checked)}
                      className="mt-0.5 accent-[var(--magenta)]"
                      data-testid="veyl-skin-in-selection"
                    />
                    <span>
                      <b>Kun hud i markert område</b>
                      <div className="text-[10px] text-[var(--cream-faint)] mt-0.5">
                        Pikelerer <b>bare huden innenfor omrissene du tegner</b> (bryst, tatoveringer). Klær og bakgrunn forblir.
                        Slå av for å skjule <b>all synlig hud</b>.
                      </div>
                    </span>
                  </label>
                )}
                <p className="text-[10px] text-[var(--cream-faint)]">
                  Følger naturlige konturer via lokal AI (MediaPipe).
                </p>
              </div>

              {mediaType === MODE_VIDEO && (
                <div className="border border-white/10 p-4 space-y-3">
                  <div className="font-meta text-xs text-[var(--cream-soft)] mb-2 tracking-[0.15em]">
                    VIDEO-INNSTILLINGER
                  </div>
                  <label className="flex items-start gap-2 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={trackRegions}
                      onChange={(e) => setTrackRegions(e.target.checked)}
                      className="mt-0.5 accent-[var(--magenta)]"
                      data-testid="veyl-track-regions"
                    />
                    <span>
                      <b>Følg tegnet område</b>
                      {cvLoading && (
                        <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-[var(--magenta)]">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Laster profesjonell tracker…
                        </span>
                      )}
                      {cvLoaded && !cvLoading && (
                        <span className="ml-2 text-[10px] text-emerald-500">✓ Profesjonell tracker klar</span>
                      )}
                      <div className="text-[10px] text-[var(--cream-faint)] mt-0.5">
                        Områder du tegner følges automatisk gjennom filmen med OpenCV optical flow.
                      </div>
                    </span>
                  </label>
                  <label className="flex items-start gap-2 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={autoTrackFaces}
                      onChange={(e) => setAutoTrackFaces(e.target.checked)}
                      className="mt-0.5 accent-[var(--magenta)]"
                      data-testid="veyl-auto-track"
                    />
                    <span>
                      <b>Følg ansikter automatisk</b>
                      <div className="text-[10px] text-[var(--cream-faint)] mt-0.5">
                        Detekterer ansikter per frame under avspilling. Kan gjøre eksport tregere.
                      </div>
                    </span>
                  </label>
                </div>
              )}

              <div className="border border-white/10 p-4 space-y-2">
                <div className="font-meta text-xs text-[var(--cream-soft)] mb-2 tracking-[0.15em]">
                  MANUELL TEGNING
                </div>
                <button
                  onClick={() => setTool(tool === "draw" ? "none" : "draw")}
                  className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-meta tracking-[0.1em] border transition ${
                    tool === "draw"
                      ? "bg-[var(--gold)] text-[var(--noir)] border-[var(--gold)]"
                      : "border-white/20 text-[var(--cream-soft)] hover:border-white/40"
                  }`}
                  data-testid="veyl-tool-draw"
                >
                  <PenLine className="w-4 h-4" />
                  {tool === "draw" ? "Tegner (aktiv)" : "Tegn omriss"}
                </button>
                <p className="text-[10px] text-[var(--cream-faint)]">
                  Tegn omriss rundt tatoveringer, arr, andre kjennetegn.
                </p>
              </div>

              <div className="border border-white/10 p-4">
                <div className="font-meta text-xs text-[var(--cream-soft)] mb-3 tracking-[0.15em]">
                  SLADDE-METODE
                </div>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <button
                    onClick={() => setMode("pixelate")}
                    className={`px-3 py-2 text-xs font-meta tracking-[0.05em] border transition ${
                      mode === "pixelate"
                        ? "bg-[var(--magenta)] text-white border-[var(--magenta)]"
                        : "border-white/20 text-[var(--cream-soft)]"
                    }`}
                    data-testid="veyl-mode-pixelate"
                  >
                    Pixel
                  </button>
                  <button
                    onClick={() => setMode("blackbox")}
                    className={`px-3 py-2 text-xs font-meta tracking-[0.05em] border transition ${
                      mode === "blackbox"
                        ? "bg-[var(--magenta)] text-white border-[var(--magenta)]"
                        : "border-white/20 text-[var(--cream-soft)]"
                    }`}
                    data-testid="veyl-mode-black"
                  >
                    Svart
                  </button>
                </div>
                {mode === "pixelate" && (
                  <>
                    <label className="font-meta text-[10px] text-[var(--cream-faint)] block mb-1">
                      Blokk-størrelse: {blockSize}
                      {blockSize <= 4 && " · maks sikkerhet"}
                      {blockSize === 8 && " · anbefalt"}
                      {blockSize >= 16 && " · minimalt"}
                    </label>
                    <input
                      type="range" min="4" max="24" step="1"
                      value={blockSize}
                      onChange={(e) => setBlockSize(parseInt(e.target.value, 10))}
                      className="w-full accent-[var(--magenta)]"
                      data-testid="veyl-block-size"
                    />
                    <p className="mt-1 text-[10px] text-[var(--cream-faint)]">
                      Lavere = færre piksler igjen = sikrere. Anbefalt 8 for ansikter.
                    </p>
                  </>
                )}
              </div>

              <div className="space-y-2">
                <button
                  onClick={() => setShowOriginal(!showOriginal)}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-xs font-meta tracking-[0.1em] border border-white/20 text-[var(--cream-soft)] hover:border-white/40 transition"
                  data-testid="veyl-toggle-original"
                >
                  {showOriginal ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  {showOriginal ? "Vis sladdet" : "Vis original"}
                </button>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={undoLast}
                    disabled={polygons.length === 0}
                    className="inline-flex items-center justify-center gap-1 px-3 py-2 text-xs font-meta border border-white/20 text-[var(--cream-soft)] hover:border-white/40 transition disabled:opacity-40"
                    data-testid="veyl-undo"
                  >
                    <Undo2 className="w-3.5 h-3.5" />
                    Angre
                  </button>
                  <button
                    onClick={clearAll}
                    disabled={!hasContent}
                    className="inline-flex items-center justify-center gap-1 px-3 py-2 text-xs font-meta border border-white/20 text-[var(--cream-soft)] hover:border-white/40 transition disabled:opacity-40"
                    data-testid="veyl-clear"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Slett alt
                  </button>
                </div>
              </div>

              <button
                onClick={download}
                disabled={busy || !hasContent}
                className="w-full inline-flex items-center justify-center gap-2 bg-[var(--gold)] text-[var(--noir)] px-4 py-3 text-sm font-meta tracking-[0.15em] hover:opacity-90 transition disabled:opacity-50 font-semibold"
                data-testid="veyl-download"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {mediaType === MODE_VIDEO ? "Eksporter video" : "Last ned"}
              </button>

              <button
                onClick={() => {
                  // Rydd opp trackers og state før ny fil
                  for (const t of trackersRef.current.values()) {
                    try { t.dispose(); } catch { /* ignorer */ }
                  }
                  trackersRef.current.clear();
                  trackedCentersRef.current.clear();
                  setMediaType(null); setImage(null); setVideoUrl(null); resetSladder();
                }}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-xs font-meta border border-white/10 text-[var(--cream-faint)] hover:border-white/30 transition"
                data-testid="veyl-new"
              >
                <Upload className="w-3.5 h-3.5" />
                Ny fil
              </button>
            </aside>
          </div>
        )}

        {!mediaType && (
          <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-4">
            <InfoCard icon={<ImageIcon className="w-5 h-5 text-[var(--magenta)]" />}
                      title="Bilder OG videoer"
                      desc="Behandle både stillbilder og film. Ansikts-tracking gjennom hele videoen." />
            <InfoCard icon={<PersonStanding className="w-5 h-5 text-[var(--magenta)]" />}
                      title="Auto-finn kropp"
                      desc="AI markerer eksponert hud automatisk. Følger naturlige konturer, ikke firkantede bokser." />
            <InfoCard icon={<Shield className="w-5 h-5 text-[var(--magenta)]" />}
                      title="Kan ikke reverseres"
                      desc="Destruktiv pikselering på 8×8-nivå. Vanlig blur kan reverseres — dette kan ikke." />
            <InfoCard icon={<Info className="w-5 h-5 text-[var(--magenta)]" />}
                      title="100 % lokalt"
                      desc="Filen forlater aldri enheten din. Ingen server, ingen sky, ingen sporing." />
          </div>
        )}
      </main>
    </div>
  );
}

function InfoCard({ icon, title, desc }) {
  return (
    <div className="border border-white/10 p-4">
      <div className="mb-2">{icon}</div>
      <div className="font-meta text-sm text-[var(--cream)] mb-1">{title}</div>
      <div className="text-xs text-[var(--cream-soft)]">{desc}</div>
    </div>
  );
}
